import { Theme, alpha } from '@mui/material'

/**
 * The app's one "liquid glass" treatment, for every sticky/fixed bar.
 *
 * Nine surfaces hand-rolled this identical block — the bottom nav, the page
 * header, the certificate's navigation header, the certificates HeaderBar, the
 * sub-view toolbar, the sticky action bar, the Activity toolbar and the Share
 * header — each repeating `blur(20px) saturate(180%)` twice (prefixed and not)
 * plus its own `@supports` fallback. Four values duplicated eight ways is how a
 * treatment drifts: change the blur radius and eight files disagree until someone
 * notices.
 *
 * `active` exists because most of these are conditional — a header is only glass
 * once it is STUCK, and transparent while it sits in the page. Passing false
 * returns the transparent state rather than nothing, so a caller can spread this
 * unconditionally and let the flag do the work.
 *
 * The `@supports` fallback is NOT optional decoration. Without backdrop-filter
 * (Firefox with it disabled, older WebKit) an 0.82-alpha background leaves page
 * content legible THROUGH the bar as it scrolls under — so the fallback goes
 * fully opaque. Losing the blur is acceptable; losing the separation is not.
 */
export const glassSx = (theme: Theme, active = true) => {
  const filter = 'blur(20px) saturate(180%)'

  return {
    backgroundColor: active ? alpha(theme.palette.background.paper, 0.82) : 'transparent',
    backdropFilter: active ? filter : 'none',
    WebkitBackdropFilter: active ? filter : 'none',
    transition: 'background-color .15s ease',
    '@supports not ((backdrop-filter: blur(1px)) or (-webkit-backdrop-filter: blur(1px)))': active
      ? { backgroundColor: theme.palette.background.paper }
      : {},
  }
}

/** The hairline a glass bar draws against the content it floats over. */
export const glassEdge = (theme: Theme) =>
  // `!important`: `#vera-shadow-root * { border-color: … }` in index.css has ID
  // specificity and silently discards an sx border colour without it.
  `1px solid ${alpha(theme.palette.common.black, 0.06)} !important`

/**
 * The same material as `glassSx`, as a PLAIN style object.
 *
 * THREE call sites set their fill through the inline `style` attribute rather than
 * `sx` — `SearchFilterSortBar`, the Activity toolbar and the Share header. (An
 * earlier version of this note said "one"; the sweep that adopted the token found
 * the other two doing the same thing for the same reason, which is itself the
 * argument for the token.) They do it because `#vera-shadow-root * { … }` in
 * index.css carries ID specificity and silently discards emotion's single-class sx
 * values — the trap documented in settings/tokens.ts — and because emotion serves a
 * CACHED class across a stuck/unstuck flip, so an sx value can paint the wrong
 * state. An inline style outranks any selector and is never class-cached, so those
 * call sites cannot use the sx form and would otherwise keep their own copy of the
 * numbers.
 *
 * `@supports` CANNOT be expressed in an inline style, so a caller using this MUST
 * still spread `glassUnsupportedSx` into its sx for the no-backdrop-filter fallback.
 * Splitting it this way keeps the blur radius and alpha in one place even where the
 * rest has to be inline.
 */
export const glassStyle = (theme: Theme, active = true) => {
  const filter = 'blur(20px) saturate(180%)'

  return {
    backgroundColor: active ? alpha(theme.palette.background.paper, 0.82) : 'transparent',
    backdropFilter: active ? filter : 'none',
    WebkitBackdropFilter: active ? filter : 'none',
  }
}

/** The `@supports` half of the treatment, for callers that must set colour inline. */
export const glassUnsupportedSx = (theme: Theme, active = true) => ({
  '@supports not ((backdrop-filter: blur(1px)) or (-webkit-backdrop-filter: blur(1px)))': active
    ? { backgroundColor: theme.palette.background.paper }
    : {},
})
